import ReactMarkdown from "react-markdown";

import { SectionCard } from "@/components/dashboard/SectionCard";
import { SectionCardChatInput } from "@/components/dashboard/SectionCardChatInput";
import { getRelevantFeedback } from "./readinessFeedbackUtils";
import { calculateDocumentScore } from "./readinessScoreUtils";
import type { TInvestorReadinessAssessment } from "./types";

type TReadinessAssessmentCardProps = {
  documentName: string;
  assessment: TInvestorReadinessAssessment;
  companyId: string;
  sectionId: string;
};

/**
 * Get score color based on score value
 */
function getScoreColor(score: number): string {
  if (score >= 80) {
    return "bg-green-100 text-green-700 border-green-300";
  }
  if (score >= 60) {
    return "bg-yellow-100 text-yellow-700 border-yellow-300";
  }
  return "bg-red-100 text-red-700 border-red-300";
}

export function ReadinessAssessmentCard({
  documentName,
  assessment,
  companyId,
  sectionId,
}: TReadinessAssessmentCardProps) {
  const score = calculateDocumentScore(documentName, assessment);
  const { gaps, recommendations } = getRelevantFeedback(documentName, assessment);
  const scoreColor = getScoreColor(score);

  // Combine feedback: summary, gaps, and recommendations
  const feedbackParts: string[] = [];

  // Add summary if available
  if (assessment.summary) {
    feedbackParts.push(`**Summary:**\n${assessment.summary}`);
  }

  // Add gaps if available
  if (gaps.length > 0) {
    feedbackParts.push(`**Gaps & Weaknesses:**\n${gaps.map((gap) => `- ${gap}`).join("\n")}`);
  }

  // Add recommendations if available
  if (recommendations.length > 0) {
    feedbackParts.push(`**Recommendations:**\n${recommendations.map((rec) => `- ${rec}`).join("\n")}`);
  }

  const feedbackText =
    feedbackParts.length > 0 ? feedbackParts.join("\n\n") : "No specific feedback available for this document type.";

  return (
    <SectionCard
      headerLeft={<h3 className="text-lg font-semibold text-foreground">{documentName}</h3>}
      headerRight={
        <div className={`flex h-12 w-12 items-center justify-center rounded-full border-2 ${scoreColor} font-semibold`}>
          <span className="text-sm">{score}/100</span>
        </div>
      }
      footer={
        <SectionCardChatInput
          mode="thread"
          companyId={companyId}
          sectionId={sectionId}
          titlePrefix={documentName}
          placeholder="Ask question..."
        />
      }
    >
      <div>
        <h4 className="mb-2 text-sm font-medium text-foreground">Feedback:</h4>
        <div className="prose prose-sm max-w-none">
          <div className="markdown-content text-sm leading-relaxed text-foreground">
            <ReactMarkdown>{feedbackText}</ReactMarkdown>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
